import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError } from "../api/client";
import { formatTimeRange, today, type TodayBoard, type TodaySession } from "../api/kiosk";
import { AdminNav } from "../components/AdminLayout";
import { useDevice } from "../context/DeviceContext";
import { useStaff } from "../context/StaffContext";
import { useClock } from "../hooks/useClock";
import "./TodayScreen.css";

/** Well inside the endpoint's 30/min limit, and fresh enough for a wall board. */
const REFRESH_MS = 60_000;

export default function TodayScreen() {
  const navigate = useNavigate();
  const { device, evict } = useDevice();
  const { isSignedIn } = useStaff();
  const now = useClock(30_000);

  const [board, setBoard] = useState<TodayBoard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // The poll restarts if the device is re-registered mid-view — rare, and the
  // correct response, since the old token is dead the moment the new one lands.
  const token = device?.kioskToken;

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const next = await today(token);
      setBoard(next);
      setError(null);
    } catch (caught) {
      if (caught instanceof ApiError && caught.isInvalidDevice) {
        evict("This device needs to be re-registered.");
        navigate("/setup", { replace: true });
        return;
      }
      // Keep the last good board on screen and note the staleness, rather than
      // blanking a wall display over one failed poll.
      setError(
        caught instanceof ApiError && caught.isNetwork
          ? "Can't reach the server — showing the last update."
          : "Couldn't refresh today's classes.",
      );
    } finally {
      setLoading(false);
    }
  }, [evict, navigate, token]);

  useEffect(() => {
    // The lint rule can't see past the async boundary: `load` awaits the network
    // before it touches state, so this is a subscription to an external system
    // (poll the board, publish each answer), not a synchronous cascading render.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
    const timer = window.setInterval(() => void load(), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const sessions = board?.sessions ?? [];

  /*
    This board is the kiosk's own ambient screen: device-token powered, never
    gated on a staff login. But an admin who walked here from the nav would be
    stranded on a page with one back button, so when — and only when — a staff
    session exists, the same nav bar is drawn instead of the plain topbar. The
    data path is identical either way.
  */
  const stamp = (
    <span className="today-date">
      {board?.branch_name || device?.branchName || ""}
      {" · "}
      {now.toLocaleDateString(undefined, {
        weekday: "long",
        day: "numeric",
        month: "short",
      })}
    </span>
  );

  return (
    <div className="screen">
      <div className="zebra-field" aria-hidden="true" />

      {isSignedIn ? (
        <AdminNav trailing={stamp} />
      ) : (
        <header className="topbar">
          <button type="button" className="btn-ghost" onClick={() => navigate("/")}>
            ← Back to kiosk
          </button>
          <span className="spacer" />
          {stamp}
        </header>
      )}

      <main className="today-stage">
        {error && (
          <p className="form-error today-error" role="status">
            {error}
          </p>
        )}

        <VisitsTile counts={board?.competition_visits} />

        {sessions.length > 0 ? (
          <div className="session-grid">
            {sessions.map((session) => (
              <SessionCard key={session.batch_id} session={session} now={now} />
            ))}
          </div>
        ) : (
          !loading && (
            <div className="today-empty">
              <span className="gold-rule" />
              <h2>No classes today</h2>
              <p className="hint">
                Nothing is scheduled at this branch for {board?.date || "today"}.
              </p>
            </div>
          )
        )}
      </main>
    </div>
  );
}

/**
 * Competition drop-ins (D-016) sit outside the session grid — a visit belongs
 * to the branch, not a class — so they get one slim strip above it. Rendered
 * only when there is something to say: most branches most days have zero, and
 * an older backend doesn't send the counts at all.
 */
function VisitsTile({ counts }: { counts?: TodayBoard["competition_visits"] }) {
  const total = Number(counts?.total_today) || 0;
  if (total === 0) return null;
  const inNow = Number(counts?.in_now) || 0;
  return (
    <aside className="visits-tile" role="status">
      <span className="visits-label">Competition drop-ins</span>
      <span className="visits-counts">
        <strong>{inNow}</strong> in now · {total} today
      </span>
    </aside>
  );
}

function SessionCard({ session, now }: { session: TodaySession; now: Date }) {
  const present = Number(session.present_count) || 0;
  const enrolled = Number(session.enrolled_count) || 0;
  const full = enrolled > 0 && present >= enrolled;
  const pct = enrolled > 0 ? Math.min(100, (present / enrolled) * 100) : 0;

  const classes = [
    "session",
    full ? "is-full" : "",
    present > 0 ? "has-present" : "",
    isLive(session, now) ? "is-live" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <article className={classes}>
      <div className="session-head">
        <h2 className="session-label">{session.label}</h2>
        <span className="session-time">
          {formatTimeRange(session.start_time, session.end_time)}
        </span>
      </div>

      <div className="session-count">
        <span className="count-present">{present}</span>
        <span className="count-of">/ {enrolled}</span>
      </div>

      <div
        className="meter"
        role="meter"
        aria-valuenow={present}
        aria-valuemin={0}
        aria-valuemax={enrolled}
        aria-label={`${session.label}: ${present} of ${enrolled} present`}
      >
        <div className="meter-fill" style={{ width: `${pct}%` }} />
      </div>

      <span className="count-caption">Present</span>
    </article>
  );
}

/**
 * Is this session running right now? Times come back as branch-local wall clock
 * ("16:00:00"), and the kiosk sits in that branch, so comparing to the device's
 * local clock is correct here — unlike on the server, where it is not.
 */
function isLive(session: TodaySession, now: Date): boolean {
  const start = minutesOf(session.start_time);
  const end = minutesOf(session.end_time);
  if (start === null) return false;
  const current = now.getHours() * 60 + now.getMinutes();
  // An open-ended batch (no end time) is live from its start.
  return current >= start && (end === null || current <= end);
}

function minutesOf(value: string | null | undefined): number | null {
  const match = /^(\d{1,2}):(\d{2})/.exec(value ?? "");
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}
